import { useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Download, Upload, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

// config: { title, noun, endpoint, sheet, fileName, columns: [[header, key, help]], samples, previewCols: [[label, key]], itemKey }
export function downloadTemplate(config) {
  const header = config.columns.map(c => c[0]);
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([header, ...config.samples]);
  ws["!cols"] = header.map(() => ({ wch: 24 }));
  XLSX.utils.book_append_sheet(wb, ws, config.sheet);
  const guide = XLSX.utils.aoa_to_sheet([["Column", "What to enter"], ...config.columns.map(c => [c[0], c[2]]), [], ["Note", `Replace or delete the sample rows on the ${config.sheet} sheet before uploading.`]]);
  guide["!cols"] = [{ wch: 24 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(wb, guide, "Instructions");
  XLSX.writeFile(wb, config.fileName);
}

const fmtCell = (v) => {
  if (!(v instanceof Date)) return v;
  if (v.getFullYear() <= 1900) { const m = Math.round(v.getHours() * 60 + v.getMinutes() + v.getSeconds() / 60) % 1440; return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; }
  const d = new Date(v.getTime() + 12 * 3600e3); // xlsx date cells can land just before local midnight
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function parseSheet(buf, config) {
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const ws = wb.Sheets[config.sheet] || wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });
  const keyFor = (h) => config.columns.find(c => c[0].toLowerCase() === String(h).trim().toLowerCase() || c[1] === String(h).trim().toLowerCase())?.[1];
  return raw
    .map((r, i) => {
      const out = { row: i + 2 };
      Object.entries(r).forEach(([h, v]) => { const k = keyFor(h); if (k) out[k] = fmtCell(v); });
      return out;
    })
    .filter(r => Object.keys(r).some(k => k !== "row" && String(r[k]).trim() !== ""));
}

export default function ExcelImportDialog({ open, onOpenChange, projectId, onImported, config, testid }) {
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const url = config.url || `/projects/${projectId}/${config.endpoint}/import`;
  const plural = (n) => `${n} ${config.noun}${n === 1 ? "" : "s"}`;

  const reset = () => { setRows(null); setPreview(null); setFileName(""); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const parsed = parseSheet(await f.arrayBuffer(), config);
      if (!parsed.length) { toast.error(`No ${config.noun} rows found. Use the template's column headings.`); return; }
      const { data } = await api.post(url, { rows: parsed, dry_run: true });
      setFileName(f.name); setRows(parsed); setPreview(data);
    } catch (err) {
      toast.error(err.response ? formatApiError(err.response.data?.detail) : "Could not read this file. Please upload an .xlsx, .xls or .csv file.");
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(url, { rows, dry_run: false });
      toast.success(`${plural(data.created)} imported${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      reset(); onOpenChange(false); onImported?.(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white border-[#E4E7EC] max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle>{config.title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadTemplate(config)} data-testid={`${testid}-template-btn`}><Download className="w-4 h-4 mr-1" /> Download template</Button>
          <label className={`inline-flex items-center gap-1 px-3 h-9 rounded-md text-sm cursor-pointer bg-orange-500 hover:bg-orange-600 text-white ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={`${testid}-file-label`}>
            <Upload className="w-4 h-4" /> {fileName ? "Choose another file" : "Choose Excel file"}
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid={`${testid}-file-input`} />
          </label>
          {fileName && <span className="text-xs font-mono text-neutral-500 truncate">{fileName}</span>}
        </div>
        {!preview && <p className="text-xs text-neutral-500">Columns: {config.columns.map(c => c[0]).join(", ")}. {config.requiredNote || `Only ${config.columns[0][0]} is required.`} You'll see a preview before anything is added.</p>}
        {preview && <PreviewTable preview={preview} config={config} testid={testid} />}
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} data-testid={`${testid}-cancel`}>Cancel</Button>
          <Button disabled={!preview?.valid || busy} onClick={doImport} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid={`${testid}-confirm`}>
            Import {plural(preview?.valid || 0)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewTable({ preview, config, testid }) {
  return (
    <div className="space-y-2" data-testid={`${testid}-preview`}>
      <div className="flex gap-3 text-xs">
        <span className="text-emerald-700 font-semibold" data-testid={`${testid}-valid-count`}>{preview.valid} ready</span>
        {preview.invalid > 0 && <span className="text-rose-600 font-semibold" data-testid={`${testid}-invalid-count`}>{preview.invalid} with errors (will be skipped)</span>}
      </div>
      <div className="border border-[#E4E7EC] rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F5F7FB] text-neutral-600 text-left">
            <tr>{["Row", ...config.previewCols.map(c => c[0]), "Check"].map(h => <th key={h} className="px-2 py-2 font-mono uppercase text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {preview.rows.map(r => (
              <tr key={r.row} className={`border-t border-[#E4E7EC] ${r.errors.length ? "bg-rose-50" : ""}`} data-testid={`${testid}-row-${r.row}`}>
                <td className="px-2 py-1.5 font-mono">{r.row}</td>
                {config.previewCols.map(([label, key], i) => <td key={label} className={`px-2 py-1.5 ${i === 0 ? "font-medium" : ""}`}>{r[config.itemKey][key] || "—"}</td>)}
                <td className="px-2 py-1.5 min-w-[180px]">
                  {r.errors.map(e => <div key={e} className="flex gap-1 text-rose-700"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{e}</div>)}
                  {r.warnings.map(w => <div key={w} className="text-amber-700">{w}</div>)}
                  {!r.errors.length && !r.warnings.length && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
